import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { customersAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import Pagination from '../components/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { Checkbox } from '../components/ui/checkbox';
import {
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Building2,
  Mail,
  Phone,
  MapPin,
  Loader2,
  Download,
  Upload,
  Paperclip,
  File,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Copy,
} from 'lucide-react';

export default function Customers() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [importLoading, setImportLoading] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [removingDuplicates, setRemovingDuplicates] = useState(false);
  const [showRemoveDuplicatesDialog, setShowRemoveDuplicatesDialog] = useState(false);
  const fileInputRef = React.useRef(null);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Multi-selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);

  const isAdmin = user?.role === 'admin';
  const totalPages = Math.ceil(totalItems / pageSize);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
  });

  // File attachment state
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const attachmentInputRef = React.useRef(null);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      const response = await customersAPI.getPaginated(params);
      const data = response.data;
      
      // Handle both paginated and array responses
      if (Array.isArray(data)) {
        setCustomers(data);
        setTotalItems(data.length);
      } else {
        setCustomers(data.items || []);
        setTotalItems(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentPage((p) => (p !== 1 ? 1 : p));
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Get sort icon for column
  const getSortIcon = (field) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" />
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const handleOpenForm = async (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        notes: customer.notes || '',
      });
      // Load existing attachments
      try {
        const res = await customersAPI.getAttachments(customer.id);
        setAttachments(res.data || []);
      } catch (error) {
        console.error('Failed to load attachments:', error);
        setAttachments([]);
      }
    } else {
      setEditingCustomer(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        notes: '',
      });
      setAttachments([]);
    }
    setPendingFiles([]);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCustomer(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      notes: '',
    });
    setAttachments([]);
    setPendingFiles([]);
  };

  const handleExportCSV = async () => {
    try {
      await customersAPI.exportCSV();
      toast.success('Customers exported successfully');
    } catch (error) {
      toast.error('Failed to export customers');
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportLoading(true);
    try {
      const text = await file.text();
      const response = await customersAPI.importCSV(text);
      const result = response.data;
      
      if (result.errors?.length > 0) {
        toast.warning(`${result.message}. ${result.errors.length} errors occurred.`);
        console.log('Import errors:', result.errors);
      } else {
        toast.success(result.message);
      }
      fetchCustomers();
      setShowImportDialog(false);
    } catch (error) {
      console.error('Import error:', error.response?.data);
      // Handle Pydantic validation errors (array of objects) or string errors
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to import customers';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Pydantic validation errors
        errorMessage = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', ');
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      
      toast.error(errorMessage);
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveDuplicates = async () => {
    setRemovingDuplicates(true);
    try {
      const response = await customersAPI.removeDuplicates();
      const result = response.data;
      
      if (result.removed_count > 0) {
        toast.success(result.message);
        fetchCustomers();
      } else {
        toast.info('No duplicate customers found');
      }
      setShowRemoveDuplicatesDialog(false);
    } catch (error) {
      console.error('Remove duplicates error:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove duplicates');
    } finally {
      setRemovingDuplicates(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      let customerId;
      
      if (editingCustomer) {
        await customersAPI.update(editingCustomer.id, formData);
        customerId = editingCustomer.id;
        toast.success('Customer updated');
      } else {
        const res = await customersAPI.create(formData);
        customerId = res.data.id;
        toast.success('Customer created');
      }
      
      // Upload pending files
      if (pendingFiles.length > 0 && customerId) {
        setUploadingFiles(true);
        let uploadedCount = 0;
        for (const file of pendingFiles) {
          try {
            await customersAPI.uploadAttachment(customerId, file);
            uploadedCount++;
          } catch (err) {
            console.error('Failed to upload file:', file.name, err);
          }
        }
        if (uploadedCount > 0) {
          toast.success(`${uploadedCount} file(s) attached`);
        }
        setUploadingFiles(false);
      }
      
      handleCloseForm();
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await customersAPI.delete(deleteId);
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    } finally {
      setDeleteId(null);
    }
  };

  // Multi-selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(customers.map(c => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (customerId, checked) => {
    if (checked) {
      setSelectedIds(prev => [...prev, customerId]);
    } else {
      setSelectedIds(prev => prev.filter(id => id !== customerId));
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkActionLoading(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const id of selectedIds) {
        try {
          await customersAPI.delete(id);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Failed to delete customer ${id}:`, error);
        }
      }
      
      if (successCount > 0) {
        toast.success(`Successfully deleted ${successCount} customer${successCount > 1 ? 's' : ''}`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to delete ${errorCount} customer${errorCount > 1 ? 's' : ''}`);
      }
      
      setSelectedIds([]);
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customers');
    } finally {
      setBulkActionLoading(false);
      setShowBulkDeleteDialog(false);
    }
  };

  // File attachment handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Add to pending files
    setPendingFiles(prev => [...prev, ...files]);
    
    // Reset input
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!editingCustomer) return;
    
    try {
      await customersAPI.deleteAttachment(editingCustomer.id, attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      toast.success('Attachment deleted');
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    if (!editingCustomer) return;
    
    try {
      const blob = await customersAPI.downloadAttachment(editingCustomer.id, attachment.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download attachment');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Customers already filtered server-side
  const filteredCustomers = customers;

  // Selection computed values
  const isAllSelected = filteredCustomers.length > 0 && 
    filteredCustomers.every(c => selectedIds.includes(c.id));
  const isSomeSelected = selectedIds.length > 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Customers</h1>
          <p className="text-muted-foreground mt-1">Manage your customer database</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-customers-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          {isAdmin && (
            <>
              <Button variant="outline" onClick={() => setShowImportDialog(true)} data-testid="import-customers-btn">
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowRemoveDuplicatesDialog(true)} 
                data-testid="remove-duplicates-btn"
                className="border-orange-300 text-orange-600 hover:bg-orange-50"
              >
                <Copy className="w-4 h-4 mr-2" />
                Remove Duplicates
              </Button>
            </>
          )}
          <Button onClick={() => handleOpenForm()} data-testid="add-customer-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Customer
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="customer-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {isAdmin && isSomeSelected && (
        <Card className="border-primary bg-primary/5">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={isAllSelected}
                  onCheckedChange={handleSelectAll}
                  data-testid="select-all-checkbox"
                />
                <span className="font-medium">
                  {selectedIds.length} customer{selectedIds.length > 1 ? 's' : ''} selected
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedIds([])}
                  className="text-muted-foreground"
                >
                  Clear selection
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBulkDeleteDialog(true)}
                  disabled={bulkActionLoading}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Customers Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="p-12 text-center">
              <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No customers found</p>
              <Button className="mt-4" onClick={() => handleOpenForm()}>
                <Plus className="w-4 h-4 mr-2" />
                Add your first customer
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && (
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={isAllSelected}
                        onCheckedChange={handleSelectAll}
                        data-testid="table-select-all"
                      />
                    </TableHead>
                  )}
                  <TableHead>
                    <button 
                      onClick={() => handleSort('name')} 
                      className="flex items-center hover:text-foreground transition-colors"
                      data-testid="sort-name"
                    >
                      Name
                      {getSortIcon('name')}
                    </button>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    <button 
                      onClick={() => handleSort('email')} 
                      className="flex items-center hover:text-foreground transition-colors"
                      data-testid="sort-email"
                    >
                      Email
                      {getSortIcon('email')}
                    </button>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">Phone</TableHead>
                  <TableHead className="hidden xl:table-cell">Address</TableHead>
                  <TableHead>
                    <button 
                      onClick={() => handleSort('created_at')} 
                      className="flex items-center hover:text-foreground transition-colors"
                      data-testid="sort-created"
                    >
                      Added
                      {getSortIcon('created_at')}
                    </button>
                  </TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.map((customer) => (
                  <TableRow 
                    key={customer.id} 
                    className={`table-row-hover ${selectedIds.includes(customer.id) ? 'bg-primary/5' : ''}`} 
                    data-testid={`customer-row-${customer.id}`}
                  >
                    {isAdmin && (
                      <TableCell>
                        <Checkbox 
                          checked={selectedIds.includes(customer.id)}
                          onCheckedChange={(checked) => handleSelectOne(customer.id, checked)}
                          data-testid={`select-customer-${customer.id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{customer.name}</p>
                          <p className="text-sm text-muted-foreground md:hidden">
                            {customer.email || 'No email'}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {customer.email ? (
                        <span className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          {customer.email}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {customer.phone ? (
                        <span className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          {customer.phone}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell">
                      {customer.address ? (
                        <span className="flex items-center gap-2 truncate max-w-[200px]">
                          <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          {customer.address}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {customer.created_at ? (
                        <span className="text-sm text-muted-foreground">
                          {format(parseISO(customer.created_at), 'MMM d, yyyy')}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`customer-menu-${customer.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenForm(customer)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {isAdmin && (
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setDeleteId(customer.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          
          {/* Pagination */}
          {!loading && totalItems > 0 && (
            <div className="border-t px-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent data-testid="customer-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingCustomer ? 'Edit Customer' : 'Add Customer'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Customer name"
                required
                data-testid="customer-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="customer@example.com"
                data-testid="customer-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1 (555) 000-0000"
                data-testid="customer-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="123 Main St, City, State"
                data-testid="customer-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes"
                rows={2}
                data-testid="customer-notes-input"
              />
            </div>
            
            {/* File Attachments Section */}
            <div className="space-y-2">
              <Label>Attachments</Label>
              <div className="border rounded-lg p-3 space-y-3">
                {/* Existing attachments (when editing) */}
                {attachments.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium">Existing Files</p>
                    {attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between p-2 bg-muted/50 rounded-md"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <File className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm truncate">{attachment.filename}</span>
                          <span className="text-xs text-muted-foreground">
                            ({formatFileSize(attachment.size)})
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadAttachment(attachment)}
                            className="h-7 w-7 p-0"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAttachment(attachment.id)}
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Pending files (to be uploaded) */}
                {pendingFiles.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium">New Files (will be uploaded on save)</p>
                    {pendingFiles.map((file, index) => (
                      <div
                        key={`${file.name}-${file.size}-${file.lastModified}`}
                        className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded-md"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span className="text-sm truncate">{file.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({formatFileSize(file.size)})
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemovePendingFile(index)}
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add file button */}
                <div>
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                    data-testid="attachment-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => attachmentInputRef.current?.click()}
                    className="w-full"
                  >
                    <Paperclip className="w-4 h-4 mr-2" />
                    Add Files
                  </Button>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || uploadingFiles} data-testid="save-customer-btn">
                {saving || uploadingFiles ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {uploadingFiles ? 'Uploading...' : 'Saving...'}
                  </>
                ) : editingCustomer ? (
                  'Update'
                ) : (
                  'Create'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this customer? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Customers from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV file with the following columns: ID (optional), Name, Email, Phone, Address, Notes
            </p>
            <p className="text-xs text-muted-foreground bg-blue-50 p-2 rounded border border-blue-200">
              <strong>Tip:</strong> If you export your customers first and then re-import, existing customers will be updated instead of creating duplicates.
            </p>
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                className="hidden"
                id="customer-csv-upload"
              />
              <label htmlFor="customer-csv-upload" className="cursor-pointer">
                <span className="text-primary hover:underline">Click to upload</span>
                <span className="text-muted-foreground"> or drag and drop</span>
              </label>
              <p className="text-xs text-muted-foreground mt-1">CSV files only</p>
            </div>
            {importLoading && (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Importing...</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Duplicates Confirmation Dialog */}
      <AlertDialog open={showRemoveDuplicatesDialog} onOpenChange={setShowRemoveDuplicatesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5 text-orange-500" />
              Remove Duplicate Customers
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  This will scan your customer database and remove duplicate entries based on email address.
                </p>
                <p>
                  For customers with the same email, the <strong>oldest record</strong> (first created) will be kept, 
                  and newer duplicates will be removed.
                </p>
                <p className="text-orange-600 font-medium">
                  This action cannot be undone. Make sure to export your customers first if you want a backup.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingDuplicates}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRemoveDuplicates}
              disabled={removingDuplicates}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {removingDuplicates ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove Duplicates'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Delete {selectedIds.length} Customer{selectedIds.length > 1 ? 's' : ''}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.length} customer{selectedIds.length > 1 ? 's' : ''}? 
              This will permanently remove their records and all associated data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkActionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              className="bg-destructive text-destructive-foreground"
              disabled={bulkActionLoading}
            >
              {bulkActionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                `Delete ${selectedIds.length} Customer${selectedIds.length > 1 ? 's' : ''}`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
